import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Icon, type IconName } from '../icon/icon'

/**
 * The Button set (210:17578): Type = Primary / Secondary / Text link, Status =
 * Default / Disabled. Disabled is react-aria's `data-disabled`, not a variant.
 * Only Primary and Text link carry an icon slot in the set, so Secondary takes no
 * `icon`. The board draws the buttons in 272px sample frames; a button hugs its
 * content.
 *
 * Figma strokes sit inside the frame, so the secondary's 1.5px accent stroke is an
 * inset ring: it does not push the 32px side padding or the 48px height.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const buttonVariants = cva(
	[
		'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-atlas-full font-atlas-sans text-atlas-body-semibold data-disabled:cursor-default',
		focusRing,
	],
	{
		variants: {
			variant: {
				primary: 'h-12 bg-atlas-accent px-8 text-atlas-light data-disabled:bg-atlas-accent-light',
				secondary:
					'h-12 bg-atlas-light px-8 text-atlas-accent inset-ring-(length:--atlas-border-width-emphasis) inset-ring-atlas-accent data-disabled:text-atlas-accent-light data-disabled:inset-ring-atlas-accent-light',
				'text-link': 'text-atlas-accent data-disabled:text-atlas-accent-light',
			},
		},
		defaultVariants: { variant: 'primary' },
	},
)

interface ButtonBaseProps extends Omit<AriaButtonProps, 'className' | 'children'> {
	className?: string
	children: ReactNode
}

/** Primary and Text link: the two types the Figma draws with an icon slot. */
interface IconButtonProps extends ButtonBaseProps {
	variant?: 'primary' | 'text-link' | null
	/**
	 * Trailing icon. Icon colours are baked into the Figma assets, so pick the one
	 * drawn for the surface: `download-01-light` on primary, `download-01` on a
	 * text link. Not rendered while disabled, as in the Figma's disabled variants.
	 */
	icon?: IconName
}

/** Secondary: the Figma draws no icon slot on it, so it takes none. */
interface SecondaryButtonProps extends ButtonBaseProps {
	variant: 'secondary'
	icon?: never
}

export type ButtonProps = IconButtonProps | SecondaryButtonProps

// Checked at runtime too, so an icon reaching a Secondary through a cast is still dropped.
const TYPES_WITH_ICON: ReadonlySet<string> = new Set(['primary', 'text-link'])

/** A pill button from the UI Kit, built on react-aria's Button. Width hugs the label. */
export function Button({ variant, icon, className, children, ...props }: ButtonProps) {
	const showIcon = icon && TYPES_WITH_ICON.has(variant ?? 'primary') && !props.isDisabled
	return (
		<AriaButton {...props} className={cn(buttonVariants({ variant }), className)}>
			{children}
			{showIcon ? <Icon name={icon} /> : null}
		</AriaButton>
	)
}
